import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Building2,
  Users,
  CreditCard,
  TrendingUp,
  Plus,
  Tag,
  UserPlus,
  Loader2,
  ChevronRight,
  Gift,
  Link,
  LogOut,
  Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformAdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Modal states
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCreatePromoModal, setShowCreatePromoModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "sales_director" });
  const [newPromo, setNewPromo] = useState({
    name: "", code: "", discount_percent: 10,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    max_uses: "",
    applicable_plans: ["starter", "professional"]
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Check if user is platform admin
    if (user && user.role !== "platform_admin") {
      toast.error("Access denied. Platform admin only.");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, companiesRes, promoRes, usersRes] = await Promise.all([
        axios.get(`${API}/platform/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/platform/companies`).catch(() => ({ data: { companies: [] } })),
        axios.get(`${API}/platform/promotions`).catch(() => ({ data: { promotions: [] } })),
        axios.get(`${API}/platform/users`).catch(() => ({ data: { users: [] } })),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data.companies || []);
      setPromotions(promoRes.data.promotions || []);
      setPlatformUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error("Failed to load platform data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/platform/users`, newUser);
      toast.success("User created successfully");
      setShowCreateUserModal(false);
      setNewUser({ name: "", email: "", password: "", role: "sales_director" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/platform/promotions`, {
        ...newPromo,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null
      });
      toast.success("Promotion created successfully");
      setShowCreatePromoModal(false);
      setNewPromo({
        name: "", code: "", discount_percent: 10,
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        max_uses: "",
        applicable_plans: ["starter", "professional"]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promotion");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-white font-semibold">MyMeetingsPal</h1>
                <p className="text-xs text-emerald-400">Platform Admin</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{user?.name}</p>
                <p className="text-xs text-stone-400">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-stone-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-stone-800 border-stone-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600">
              Overview
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-emerald-600">
              Companies
            </TabsTrigger>
            <TabsTrigger value="compliance" className="data-[state=active]:bg-emerald-600">
              Compliance
            </TabsTrigger>
            <TabsTrigger value="promotions" className="data-[state=active]:bg-emerald-600">
              Promotions
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-emerald-600">
              Platform Users
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-stone-800 border border-stone-700 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-stone-400 text-sm">Companies</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.total_companies || 0}</p>
              </div>
              
              <div className="bg-stone-800 border border-stone-700 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-stone-400 text-sm">Total Users</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
              </div>
              
              <div className="bg-stone-800 border border-stone-700 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard className="w-5 h-5 text-amber-500" />
                  <span className="text-stone-400 text-sm">Active Subs</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.active_subscriptions || 0}</p>
              </div>
              
              <div className="bg-stone-800 border border-stone-700 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <span className="text-stone-400 text-sm">Monthly Revenue</span>
                </div>
                <p className="text-3xl font-bold text-white">${stats?.monthly_revenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            {/* Top Referrers */}
            <div className="bg-stone-800 border border-stone-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Top Referrers</h3>
              {stats?.top_referrers?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_referrers.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-stone-700/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{ref.sales_director_name}</p>
                        <p className="text-sm text-stone-400">Code: {ref.code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-semibold">{ref.total_signups} signups</p>
                        <p className="text-sm text-stone-400">${ref.total_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500 text-center py-4">No referrals yet</p>
              )}
            </div>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="mt-6">
            <div className="bg-stone-800 border border-stone-700 rounded-lg">
              <div className="p-4 border-b border-stone-700">
                <h3 className="text-lg font-semibold text-white">All Companies</h3>
              </div>
              <div className="divide-y divide-stone-700">
                {companies.length > 0 ? (
                  companies.map((company) => (
                    <div key={company.id} className="p-4 flex items-center justify-between hover:bg-stone-700/50">
                      <div className="flex items-center gap-4">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-stone-700 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-stone-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{company.name}</p>
                          <p className="text-sm text-stone-400">{company.slug}.mymeetingspal.com</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          company.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                          company.subscription_status === 'trialing' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {company.subscription_status}
                        </span>
                        <span className="text-sm text-stone-400">{company.plan_tier}</span>
                        <ChevronRight className="w-4 h-4 text-stone-500" />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-500 text-center py-8">No companies yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="mt-6">
            <div className="bg-stone-800 border border-stone-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Compliance Management</h3>
                  <p className="text-sm text-stone-400 mt-1">Manage jurisdiction profiles and assign compliance rules to companies</p>
                </div>
                <Button
                  onClick={() => navigate("/platform/compliance")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="manage-compliance-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Manage Compliance
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-stone-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-emerald-500" />
                    <span className="text-stone-300">Profiles</span>
                  </div>
                  <p className="text-2xl font-bold text-white">4</p>
                  <p className="text-xs text-stone-500">System + Custom</p>
                </div>
                <div className="bg-stone-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    <span className="text-stone-300">Assigned</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{companies.filter(c => c.compliance_profile_id).length}</p>
                  <p className="text-xs text-stone-500">Companies with profiles</p>
                </div>
                <div className="bg-stone-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-amber-500" />
                    <span className="text-stone-300">Default</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{companies.filter(c => !c.compliance_profile_id).length}</p>
                  <p className="text-xs text-stone-500">Using default profile</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions" className="mt-6">
            <div className="bg-stone-800 border border-stone-700 rounded-lg">
              <div className="p-4 border-b border-stone-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Promotions</h3>
                <Button
                  size="sm"
                  onClick={() => setShowCreatePromoModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Promotion
                </Button>
              </div>
              <div className="divide-y divide-stone-700">
                {promotions.length > 0 ? (
                  promotions.map((promo) => (
                    <div key={promo.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                          <Gift className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{promo.name}</p>
                          <p className="text-sm text-stone-400">Code: <span className="font-mono">{promo.code}</span></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-emerald-400 font-semibold">{promo.discount_percent}% off</span>
                        <span className="text-sm text-stone-400">{promo.current_uses} uses</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          promo.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-stone-500/20 text-stone-400'
                        }`}>
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-500 text-center py-8">No promotions yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Platform Users Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="bg-stone-800 border border-stone-700 rounded-lg">
              <div className="p-4 border-b border-stone-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Platform Users</h3>
                <Button
                  size="sm"
                  onClick={() => setShowCreateUserModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add User
                </Button>
              </div>
              <div className="divide-y divide-stone-700">
                {platformUsers.length > 0 ? (
                  platformUsers.map((u) => (
                    <div key={u.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-stone-700 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">{u.name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="text-sm text-stone-400">{u.email}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.role === 'platform_admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {u.role === 'platform_admin' ? 'Platform Admin' : 'Sales Director'}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-500 text-center py-8">No platform users yet</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Platform User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div>
              <Label className="text-stone-300">Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-stone-300">Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-stone-300">Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-stone-300">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales_director">Sales Director</SelectItem>
                  <SelectItem value="platform_admin">Platform Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create User"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Promotion Modal */}
      <Dialog open={showCreatePromoModal} onOpenChange={setShowCreatePromoModal}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Promotion</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePromo} className="space-y-4 mt-4">
            <div>
              <Label className="text-stone-300">Promotion Name</Label>
              <Input
                value={newPromo.name}
                onChange={(e) => setNewPromo({ ...newPromo, name: e.target.value })}
                placeholder="e.g., Summer Sale"
                required
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <Label className="text-stone-300">Code</Label>
              <Input
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SUMMER25"
                required
                className="bg-stone-700 border-stone-600 text-white font-mono"
              />
            </div>
            <div>
              <Label className="text-stone-300">Discount Percent</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newPromo.discount_percent}
                onChange={(e) => setNewPromo({ ...newPromo, discount_percent: parseInt(e.target.value) })}
                required
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Valid From</Label>
                <Input
                  type="date"
                  value={newPromo.valid_from}
                  onChange={(e) => setNewPromo({ ...newPromo, valid_from: e.target.value })}
                  required
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
              <div>
                <Label className="text-stone-300">Valid Until</Label>
                <Input
                  type="date"
                  value={newPromo.valid_until}
                  onChange={(e) => setNewPromo({ ...newPromo, valid_until: e.target.value })}
                  required
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-stone-300">Max Uses (leave empty for unlimited)</Label>
              <Input
                type="number"
                min="1"
                value={newPromo.max_uses}
                onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Promotion"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
